import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Star, Quote, Award, Calendar, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapping for review sources (badge label + color)
const SOURCE_BADGE = {
  google:      { label: "Google",      cls: "bg-amber-100 text-amber-900 border-amber-400" },
  booking:     { label: "Booking.com", cls: "bg-blue-100 text-blue-900 border-blue-400" },
  airbnb:      { label: "Airbnb",      cls: "bg-rose-100 text-rose-900 border-rose-400" },
  tripadvisor: { label: "Tripadvisor", cls: "bg-emerald-100 text-emerald-900 border-emerald-400" },
  manual:      { label: "Diretta",     cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

// All reviews with date (YYYY-MM-DD) + rating (0-10) + optional title.
// The most recent 10/10 with a rich text becomes the featured review automatically.
const REVIEWS = {
  it: [
    { name: "Eros", country: "Italia", date: "2026-01-22", rating: 10, title: "Soggiorno superlativo", text: "La stanza fresca e dotata di tutto, ottima la sala colazioni con ampia scelta. Apprezzatissima la 'torre dell'acqua' sempre a disposizione! La proprietaria attentissima ai dettagli, posizione strategica per visitare il centro e la costa." },
    { name: "Andrea", country: "Italia", date: "2025-11-08", rating: 10, text: "Sia la titolare che il personale sono stati disponibilissimi e gentilissimi. Colazione buona. La struttura è pulitissima e profumata." },
    { name: "Antonio", country: "Italia", date: "2025-09-14", rating: 10, text: "Belle stanze ampie e accoglienti. Non mancava nulla. Davvero ottimo rapporto qualità/prezzo." },
    { name: "Michela", country: "Italia", date: "2025-08-03", rating: 10, text: "Personale cordiale e disponibilissimo, struttura pulita e accogliente. A richiesta, c'è il servizio di navetta da/verso l'aeroporto." },
    { name: "Monica", country: "Italia", date: "2025-07-21", rating: 9, text: "Proprietaria gentilissima. Ottima colazione e abbondante. Buon rapporto qualità prezzo." },
    { name: "Erika", country: "Italia", date: "2025-06-12", rating: 10, text: "La proprietaria è stata molto carina e molto disponibile. Indicazioni dettagliate. Pulita e accogliente. A 5 min di macchina sei in centro." },
  ],
  en: [
    { name: "Eros", country: "Italy", date: "2026-01-22", rating: 10, title: "Outstanding stay", text: "Fresh, fully-equipped room and excellent breakfast room with a wide choice. The always-available 'water station' was a delightful touch! The owner is attentive to every detail, strategic location to visit the centre and the coast." },
    { name: "Andrea", country: "Italy", date: "2025-11-08", rating: 10, text: "The owner and staff were very helpful and kind. Good breakfast. The property is spotlessly clean and fragrant." },
    { name: "Antonio", country: "Italy", date: "2025-09-14", rating: 10, text: "Beautiful large and welcoming rooms. Nothing missing. Truly excellent value for money." },
    { name: "Michela", country: "Italy", date: "2025-08-03", rating: 10, text: "Friendly, very helpful staff, clean and welcoming property. On request, airport shuttle service is available." },
    { name: "Monica", country: "Italy", date: "2025-07-21", rating: 9, text: "Very kind owner. Excellent and generous breakfast. Great value for money." },
    { name: "Erika", country: "Italy", date: "2025-06-12", rating: 10, text: "The owner was very kind and helpful. Detailed directions. Clean and welcoming. 5 minutes by car from the city center." },
  ],
  de: [
    { name: "Eros", country: "Italien", date: "2026-01-22", rating: 10, title: "Hervorragender Aufenthalt", text: "Frisches, voll ausgestattetes Zimmer und ausgezeichneter Frühstücksraum mit großer Auswahl. Die stets verfügbare 'Wasserstation' war eine schöne Aufmerksamkeit! Die Gastgeberin achtet auf jedes Detail, ideale Lage für Stadt und Küste." },
    { name: "Andrea", country: "Italien", date: "2025-11-08", rating: 10, text: "Gastgeberin und Personal waren sehr hilfsbereit und freundlich. Gutes Frühstück. Die Unterkunft ist blitzsauber und duftet." },
    { name: "Antonio", country: "Italien", date: "2025-09-14", rating: 10, text: "Schöne, große und einladende Zimmer. Hervorragendes Preis-Leistungs-Verhältnis." },
    { name: "Michela", country: "Italien", date: "2025-08-03", rating: 10, text: "Freundliches, sehr hilfsbereites Personal, saubere Unterkunft. Flughafentransfer auf Anfrage." },
    { name: "Monica", country: "Italien", date: "2025-07-21", rating: 9, text: "Sehr nette Gastgeberin. Ausgezeichnetes und reichliches Frühstück." },
    { name: "Erika", country: "Italien", date: "2025-06-12", rating: 10, text: "Sehr freundliche Gastgeberin mit detaillierten Anweisungen. Sauber und einladend. 5 Min. mit dem Auto zum Zentrum." },
  ],
  fr: [
    { name: "Eros", country: "Italie", date: "2026-01-22", rating: 10, title: "Séjour exceptionnel", text: "Chambre fraîche et bien équipée, excellente salle de petit-déjeuner avec beaucoup de choix. La 'station d'eau' toujours disponible est très appréciée ! Propriétaire attentive aux détails, emplacement stratégique pour visiter le centre et la côte." },
    { name: "Andrea", country: "Italie", date: "2025-11-08", rating: 10, text: "La propriétaire et le personnel ont été très serviables et gentils. Bon petit-déjeuner. L'établissement est impeccable et parfumé." },
    { name: "Antonio", country: "Italie", date: "2025-09-14", rating: 10, text: "Belles chambres spacieuses et accueillantes. Rien ne manquait. Excellent rapport qualité/prix." },
    { name: "Michela", country: "Italie", date: "2025-08-03", rating: 10, text: "Personnel cordial et très disponible, établissement propre et accueillant. Service de navette aéroport sur demande." },
    { name: "Monica", country: "Italie", date: "2025-07-21", rating: 9, text: "Propriétaire très gentille. Excellent et copieux petit-déjeuner. Bon rapport qualité-prix." },
    { name: "Erika", country: "Italie", date: "2025-06-12", rating: 10, text: "Propriétaire très gentille et serviable. Indications détaillées. Propre et accueillant. À 5 min en voiture du centre." },
  ],
};

const HEADINGS = {
  it: { overline: "Recensioni", title: "La voce degli ospiti", score: "Punteggio", reviews: "recensioni", featured_label: "Recensione in evidenza", more: "Altre recensioni recenti", on: "Su 10", see_all: "Vedi tutte", all_title: "Tutte le recensioni dei nostri ospiti", close: "Chiudi" },
  en: { overline: "Reviews", title: "What our guests say", score: "Score", reviews: "reviews", featured_label: "Featured review", more: "More recent reviews", on: "Out of 10", see_all: "See all", all_title: "All guest reviews", close: "Close" },
  de: { overline: "Bewertungen", title: "Was unsere Gäste sagen", score: "Bewertung", reviews: "Bewertungen", featured_label: "Hervorgehobene Bewertung", more: "Weitere aktuelle Bewertungen", on: "Von 10", see_all: "Alle ansehen", all_title: "Alle Gästebewertungen", close: "Schließen" },
  fr: { overline: "Avis", title: "La voix de nos invités", score: "Note", reviews: "avis", featured_label: "Avis mis en avant", more: "Autres avis récents", on: "Sur 10", see_all: "Voir tout", all_title: "Tous les avis de nos invités", close: "Fermer" },
};

const MONTH_NAMES = {
  it: ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
  fr: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
};

const formatMonth = (iso, lang) => {
  const [y, m] = iso.split("-");
  const months = MONTH_NAMES[lang] || MONTH_NAMES.it;
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};

const Reviews = () => {
  const { lang } = useLang();
  const h = HEADINGS[lang] || HEADINGS.it;
  const fallback = REVIEWS[lang] || REVIEWS.it;

  const [dynamic, setDynamic] = useState([]);
  const [googleMeta, setGoogleMeta] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Ask the backend for plenty of reviews (we keep the best ones for the modal)
    axios.get(`${API}/reviews/public?limit=40&min_rating=4`).then((r) => {
      if (cancelled) return;
      const items = (r.data?.reviews || []).map((rv) => ({
        name: rv.author_name,
        country: rv.author_origin || (rv.source === "google" ? "Google" : ""),
        date: (rv.published_at || rv.created_at || "").slice(0, 10),
        rating: (rv.rating || 0) * 2, // 1-5 → 1-10 to match hardcoded scale
        text: rv.text || "",
        source: rv.source,
        url: rv.url,
        is_featured: !!rv.is_featured,
      }));
      setDynamic(items);
      setGoogleMeta(r.data?.google || null);
    }).catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Merge dynamic (from DB) + hardcoded fallback, keeping only reviews with
  // actual text (Google sometimes returns star-only reviews with empty text →
  // those cards look broken on the homepage). Then sort with the best/most
  // recent first.
  const allWithText = useMemo(() => {
    const source = dynamic.length ? [...dynamic, ...fallback] : fallback;
    return source.filter((r) => (r.text || "").trim().length >= 15);
  }, [dynamic, fallback]);

  // Featured = highest rating, then most recent date. Substantive reviews preferred (>=140 chars).
  const { featured, others, allBest } = useMemo(() => {
    const sorted = [...allWithText].sort((a, b) => {
      // explicit featured flag wins
      if ((b.is_featured ? 1 : 0) !== (a.is_featured ? 1 : 0)) return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
      if (b.rating !== a.rating) return b.rating - a.rating;
      const aRich = (a.text || "").length >= 140 ? 1 : 0;
      const bRich = (b.text || "").length >= 140 ? 1 : 0;
      if (aRich !== bRich) return bRich - aRich;
      return (b.date || "").localeCompare(a.date || "");
    });
    return {
      featured: sorted[0],
      others: sorted.slice(1, 7),    // show 6 cards on the homepage
      allBest: sorted,                // full list for the "Vedi tutte" modal
    };
  }, [allWithText]);

  // Allow other components (e.g. the Hero rating badge) to open this modal
  // without needing a shared context — fire a custom DOM event.
  useEffect(() => {
    const handler = () => setShowAll(true);
    window.addEventListener("open-reviews-modal", handler);
    return () => window.removeEventListener("open-reviews-modal", handler);
  }, []);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!showAll) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [showAll]);

  if (!featured) return null;

  const filledStars = Math.round(featured.rating / 2);
  const scoreLabel = googleMeta?.rating_avg ? googleMeta.rating_avg.toFixed(1) : "7,5";
  const reviewsCount = googleMeta?.rating_count || 182;

  return (
    <section id="reviews" className="py-20 md:py-28 bg-[#FDFBF7]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-overline mb-3">{h.overline}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#1E3A5F] leading-tight">{h.title}</h2>
          </div>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="flex items-center gap-4 bg-white border border-[#E8E2D9] rounded-xl px-5 py-4 hover:border-[#AC5D45] hover:shadow-md transition-all text-left cursor-pointer"
            data-testid="reviews-summary"
            aria-label={h.see_all}
          >
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-[#635F59]">{h.score}</div>
              <div className="font-display text-4xl font-semibold text-[#AC5D45]">{scoreLabel}</div>
            </div>
            <div className="w-px h-10 bg-[#E8E2D9]" />
            <div>
              <div className="flex text-[#E3A857]">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-[#E3A857]" />
                ))}
              </div>
              <div className="text-xs text-[#635F59] mt-1">
                <span className="underline decoration-dotted">{reviewsCount} {h.reviews}</span>
                <span className="ml-1 text-[#AC5D45] font-semibold">→</span>
              </div>
              <div className="text-[10px] text-[#AC5D45] mt-0.5">{h.see_all}</div>
            </div>
          </button>
        </div>

        {/* FEATURED REVIEW (auto-selected) */}
        <article
          data-testid="featured-review"
          className="relative mb-12 bg-gradient-to-br from-[#1E3A5F] to-[#0F2440] text-white rounded-2xl p-8 md:p-10 overflow-hidden shadow-xl"
        >
          <Quote className="absolute -top-4 -right-4 w-44 h-44 text-white/[0.04] rotate-180" />

          <div className="inline-flex items-center gap-2 bg-[#E3A857] text-[#1E3A5F] text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full mb-5" data-testid="featured-badge">
            <Award className="w-3.5 h-3.5" />
            {h.featured_label}
          </div>

          <div className="grid md:grid-cols-12 gap-8 items-start relative z-10">
            <div className="md:col-span-9">
              {featured.title && (
                <h3 className="font-display text-2xl md:text-3xl mb-4 text-white" data-testid="featured-title">&ldquo;{featured.title}&rdquo;</h3>
              )}
              <p className="text-white/85 text-base md:text-lg leading-relaxed font-display italic mb-6" data-testid="featured-text">
                {featured.text}
              </p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-5 border-t border-white/15">
                <div>
                  <div className="font-semibold text-white text-base" data-testid="featured-name">{featured.name}</div>
                  <div className="text-xs text-white/60">{featured.country}</div>
                </div>
                {featured.date && (
                  <div className="flex items-center gap-1.5 text-xs text-white/70" data-testid="featured-date">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatMonth(featured.date, lang)}
                  </div>
                )}
                {featured.source && SOURCE_BADGE[featured.source] && (
                  <span className="text-[10px] uppercase tracking-wider bg-white/10 text-white/80 px-2 py-0.5 rounded border border-white/15">
                    via {SOURCE_BADGE[featured.source].label}
                  </span>
                )}
              </div>
            </div>

            <aside className="md:col-span-3 md:border-l md:border-white/15 md:pl-8 flex md:flex-col items-center md:items-start gap-3">
              <div className="font-display text-5xl md:text-6xl font-semibold text-[#E3A857] leading-none" data-testid="featured-rating">
                {featured.rating}
                <span className="text-2xl text-white/40">/10</span>
              </div>
              <div className="flex text-[#E3A857]">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className={`w-4 h-4 ${s < filledStars ? "fill-[#E3A857]" : "text-white/20"}`} />
                ))}
              </div>
            </aside>
          </div>
        </article>

        {/* OTHER REVIEWS */}
        <p className="text-overline mb-6" data-testid="more-reviews-overline">{h.more}</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {others.map((r, i) => {
            const f = Math.round(r.rating / 2);
            const srcBadge = r.source && SOURCE_BADGE[r.source];
            return (
              <article
                key={`${r.name}-${i}`}
                data-testid={`review-${i}`}
                className="bg-white border border-[#E8E2D9] rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <Quote className="w-6 h-6 text-[#AC5D45]/40" />
                  {srcBadge && (
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${srcBadge.cls}`}>
                      {srcBadge.label}
                    </span>
                  )}
                </div>
                <p className="text-[#2D2926] leading-relaxed text-sm mb-4">&ldquo;{r.text}&rdquo;</p>
                <div className="flex items-center justify-between pt-4 border-t border-[#E8E2D9]">
                  <div>
                    <div className="font-semibold text-[#1E3A5F]">{r.name}</div>
                    <div className="text-xs text-[#635F59]">{r.country}{r.date ? ` · ${formatMonth(r.date, lang)}` : ""}</div>
                  </div>
                  <div className="flex text-[#E3A857]" title={`${r.rating}/10`}>
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star key={s} className={`w-3 h-3 ${s < f ? "fill-[#E3A857]" : "text-[#E8E2D9]"}`} />
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {/* MODAL — full list of best reviews */}
      {showAll && (
        <div
          className="fixed inset-0 z-[100] flex items-start md:items-center justify-center bg-black/60 backdrop-blur-sm p-3 md:p-8 overflow-y-auto"
          onClick={() => setShowAll(false)}
          data-testid="reviews-modal-backdrop"
        >
          <div
            role="dialog"
            aria-modal="true"
            className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl my-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="reviews-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E8E2D9] bg-gradient-to-r from-[#1E3A5F] to-[#0F2440] text-white">
              <div>
                <p className="text-overline text-[#E3A857] mb-1">{h.overline}</p>
                <h3 className="font-display text-xl md:text-2xl">{h.all_title}</h3>
                <div className="flex items-center gap-2 mt-2 text-sm text-white/80">
                  <div className="flex text-[#E3A857]">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-[#E3A857]" />
                    ))}
                  </div>
                  <span>{scoreLabel}</span>
                  <span className="text-white/40">·</span>
                  <span>{reviewsCount} {h.reviews}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                aria-label={h.close}
                data-testid="reviews-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto px-5 md:px-8 py-6 bg-[#FDFBF7]">
              <div className="grid sm:grid-cols-2 gap-4">
                {allBest.map((r, i) => {
                  const f = Math.round(r.rating / 2);
                  const srcBadge = r.source && SOURCE_BADGE[r.source];
                  return (
                    <article
                      key={`modal-${r.name}-${i}`}
                      className="bg-white border border-[#E8E2D9] rounded-xl p-5"
                      data-testid={`modal-review-${i}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <Quote className="w-5 h-5 text-[#AC5D45]/40" />
                        {srcBadge && (
                          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${srcBadge.cls}`}>
                            {srcBadge.label}
                          </span>
                        )}
                      </div>
                      {r.title && (
                        <p className="font-semibold text-[#1E3A5F] mb-2">&ldquo;{r.title}&rdquo;</p>
                      )}
                      <p className="text-[#2D2926] leading-relaxed text-sm mb-4">&ldquo;{r.text}&rdquo;</p>
                      <div className="flex items-center justify-between pt-3 border-t border-[#E8E2D9]">
                        <div>
                          <div className="font-semibold text-[#1E3A5F] text-sm">{r.name}</div>
                          <div className="text-[11px] text-[#635F59]">{r.country}{r.date ? ` · ${formatMonth(r.date, lang)}` : ""}</div>
                        </div>
                        <div className="flex text-[#E3A857]" title={`${r.rating}/10`}>
                          {[0, 1, 2, 3, 4].map((s) => (
                            <Star key={s} className={`w-3 h-3 ${s < f ? "fill-[#E3A857]" : "text-[#E8E2D9]"}`} />
                          ))}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
              {googleMeta?.place_url && (
                <div className="text-center mt-8">
                  <a
                    href={googleMeta.place_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#AC5D45] hover:underline text-sm"
                  >
                    Vedi tutte le {reviewsCount} recensioni su Google →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Reviews;
